/** @format */

/* eslint require-await: "warn" -- Legacy code: do not use as an example. */
import { merge } from 'lodash';

import {
    getModelIdFromSlugQuery,
    getModelStockInfoQuery,
} from '@toucan/common/services/search/queries';
import { getSemanticSearchResults } from '@toucan/common/services/search/requests';
import { getSearchQueryParams } from '@toucan/common/services/search/search-service';
import {
    transformModelIdFromSlugResponse,
    transformModelStockInfoResponse,
} from '@toucan/common/services/search/serializers';
import {
    AdvancedSuggestions,
    FilterQuery,
    ModelStockInfo,
    Query,
    QueryResponse,
    SearchListingModel,
    SearchListingProduct,
    SearchTermModel,
    SearchValue,
    SortBy,
    StockInfoAPIResponse,
    SuperSkusData,
} from '@toucan/common/services/search/types';

import { hasValue } from '@toucan/client/global/application';
import { client } from '@toucan/client/global/axios';
import {
    Market,
    MarketCode,
    MarketIso,
    defaultMarket,
    euMarket,
    europeanMarkets,
} from '@toucan/client/global/market';
import { ModelAccessory } from '@toucan/client/global/model';
import { ItemId, ProductConditionValue } from '@toucan/client/global/product';

import {
    QueryResultFields,
    Suggestion,
} from '@toucan/client/core/search/config/types';
import { escapeDoubleQuotes } from '@toucan/client/core/search/helpers';

export type { SearchTermModel };

/**
 * Get model suggestions by search term from the suggestions endpoint
 *
 * @param searchTerm The term to be searched
 * @param locale The locale to search for results in. E.g. en_GB
 * @param count How many suggestions should be fetched? Default: 10
 */
export async function getModelSuggestions(
    searchTerm: string,
    locale: string = defaultMarket.iso,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    count: number = 20
): Promise<Suggestion[]> {
    const response = await client.get(
        '/search-service/product/suggest/model/fuzzy/',
        {
            params: {
                query: searchTerm,
                count,
            },
            headers: {
                'Content-Language': locale,
            },
        }
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
    return response.data.suggestions;
}

export async function getListingResults<T>(
    search: Query,
    locale: string,
    marketCode: MarketCode
): Promise<QueryResponse<T>> {
    return getSearchResults<T>(search, locale, marketCode);
}

export async function getSemanticListingResults(
    search: Query,
    locale: MarketIso,
    marketCode: MarketCode
): Promise<QueryResponse<SearchListingModel>> {
    return getSemanticSearchResults(search, locale, marketCode);
}

/**
 * Get a model by a search term
 *
 * @param {string} searchTerm The term to search a model for
 * @param {string} locale The locale to search for results in. E.g. en_GB
 * @param {MarketCode} market The market code
 */
export async function getModelBySearchTerm(
    searchTerm: string,
    locale: string = defaultMarket.iso,
    marketCode: MarketCode = defaultMarket.code
): Promise<SearchTermModel> {
    const search: Query = {
        fieldList: [
            'model_id',
            'model_name',
            'model_images',
            'model_available_new',
        ],
        filterQuery: {
            modelMarket: marketCode.toLowerCase(),
            modelName: `"${escapeDoubleQuotes(searchTerm)}"`,
        },
        rows: 1,
    };
    const data = await getSearchResults(search, locale, marketCode);

    const modelResults = (data.results as QueryResultFields[]).map(
        (result: QueryResultFields) => ({
            id: result.modelId.values[0],
            name: result.modelName.values[0],
            image: result?.modelImages?.values[0] ?? '',
            availableNew: result?.modelAvailableNew?.values[0] === 'True',
        })
    );
    return modelResults[0];
}

export async function getPurchasableModelsById(
    modelIds: string[],
    locale: string = defaultMarket.iso,
    marketCode: MarketCode = defaultMarket.code
): Promise<SearchTermModel[]> {
    const search: Query = {
        fieldList: [
            'model_id',
            'model_name',
            'model_images',
            'model_purchased_by_mpb',
        ],
        filterQuery: {
            modelMarket: marketCode.toLowerCase(),
            objectType: 'model',
            modelId: `${modelIds.join(' OR ')}`,
            modelPurchasedByMpb: true,
        },
    };
    const data = await getSearchResults(search, locale, marketCode);
    const modelResults = (data.results as QueryResultFields[]).map(
        (result: QueryResultFields) => ({
            id: result.modelId.values[0],
            name: result.modelName.values[0],
            image: result?.modelImages?.values[0] ?? '',
            purchasedByMpb: hasValue(result?.modelPurchasedByMpb?.values[0])
                ? true
                : false,
        })
    );

    return modelResults;
}

/**
 * Get a products by model id and condition
 *
 * @param {number} modelId - The model id of the products to be returned
 * @param {ProductConditionValue} condition - The condition of the products to be returned
 * @param {ItemId[]} skuBlacklist - An array of skus to be excluded from the results
 * @param {string} [locale = defaultMarket.iso] - The locale to search within
 * @param {MarketCode} [market = defaultMarket.code] - The market to search within
 * @returns {QueryResponse} - Results from the search service
 */
export async function getProductsByModelIdAndCondition(
    modelId: number,
    condition: ProductConditionValue,
    skuBlacklist: ItemId[],
    locale: string = defaultMarket.iso,
    marketCode: MarketCode = defaultMarket.code
): Promise<QueryResponse> {
    const search: Query = {
        fieldList: [
            'model_id',
            'model_name',
            'model_images',
            'model_url_segment',
            'product_sku',
            'product_price',
            'product_condition',
        ],
        filterQuery: {
            objectType: 'PRODUCT',
            productMarket: marketCode.toUpperCase(),
            modelId,
            productCondition: condition.toUpperCase(),
            productSku: `NOT (${skuBlacklist.join(' OR ')})`,
        },
    };

    return await getSearchResults(search, locale, marketCode);
}

/**
 * Get model suggestions by search term from the search endpoint
 *
 * @param {string} searchTerm The term to search a model for
 * @param {string} locale The locale to search for results in. E.g. en_GB
 * @param {MarketCode} market The market code
 */
export async function getModelSearchSuggestionsAdvanced(
    searchTerm: string,
    locale: string = defaultMarket.iso,
    marketCode: MarketCode = defaultMarket.code
): Promise<AdvancedSuggestions> {
    const search: Query = {
        query: searchTerm,
        fieldList: ['model_name'],
        filterQuery: {
            modelMarket: marketCode.toLowerCase(),
            objectType: 'MODEL',
            modelIsPublishedIn: true,
        },
        rows: 50,
    };

    const data = await getSearchResults(search, locale, marketCode);

    const suggestions = (data.results as QueryResultFields[]).reduce<
        Suggestion[]
    >((suggestions, result) => {
        if (result.modelName?.values?.[0]) {
            suggestions.push({
                term: result.modelName.values[0],
            });
        }

        return suggestions;
    }, []);

    const suggestionCount = suggestions.length;
    const spellcheckSearchTerm = data.spellcheck.suggestions[0];

    return {
        spellcheckSearchTerm,
        suggestionCount,
        suggestions,
    };
}

/**
 * Get a model query
 *
 * @param {Query} baseQuery the base query
 * @param {number} start the first result in the set to fetch
 * @param {number} rows the number of result rows to fetch
 * @param {FilterQuery} filterQuery the filters to apply to the search
 * @param {SortBy} sortBy How the search should be sorted
 * @param {MarketCode} market The market code
 * @param {string[]} facetFields
 * @param {boolean} showOutOfStock whether to show out of stock products
 */
export function getProductListingQuery(
    baseQuery: Query,
    start: number,
    rows: number,
    filterQuery: FilterQuery,
    sortBy: SortBy,
    market: MarketCode = defaultMarket.code,
    facetFields?: string[],
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    showOutOfStock: boolean = false
): Query {
    return merge({}, baseQuery, {
        fieldList: [
            'model_name',
            'model_description',
            'product_price',
            'model_url_segment',
            'product_sku',
            'product_condition',
            'product_shutter_count',
            'product_hour_count',
            'product_battery_charge_count',
            'product_id',
            'product_images',
            'model_id',
            'product_price_reduction',
            'product_price_original',
            'product_price_modifiers',
            'model_available_new',
        ],
        filterQuery: {
            modelMarket: market,
            objectType: 'product',
            modelAvailable: !showOutOfStock,
            modelIsPublishedOut: true,
            ...filterQuery,
        },
        sort: sortBy,
        facetMinimumCount: 1,
        facetField: facetFields,
        start,
        rows,
    });
}

export async function getModelIdFromSlug(
    slug: string,
    marketCode: MarketCode,
    locale: string
): Promise<string | undefined> {
    const search: Query = getModelIdFromSlugQuery(slug, marketCode);

    const response: QueryResponse<SearchListingModel> = await getSearchResults(
        search,
        locale,
        marketCode
    );

    return transformModelIdFromSlugResponse(response);
}

/**
 * Get the number of models currently in stock and the min and max prices.
 *
 * @param {string} modelId the model ID
 * @param {Market} activeMarket The active market
 * @param {number | undefined} productIdToExclude The product id we want to exclude from the count
 */
export async function getModelStockInfo(
    modelId: string,
    activeMarket: Market,
    productIdToExclude?: number
): Promise<ModelStockInfo> {
    const { iso: locale } = activeMarket;

    const search = getModelStockInfoQuery(
        modelId,
        activeMarket,
        productIdToExclude
    );
    const response: QueryResponse<StockInfoAPIResponse> =
        await getSearchResults(search, locale, activeMarket.code);

    return transformModelStockInfoResponse(response?.results[0], activeMarket);
}

type AvailableModelAccessoriesResponse = {
    modelId: SearchValue;
    productPriceCount: SearchValue;
};

// eslint-disable-next-line @mpb-com/mpb/pure-request-functions -- Legacy code: do not use as an example.
export const getAvailableModelAccessories = async (
    accessories: ModelAccessory[],
    activeMarket: Market
) => {
    if (accessories.length === 0) {
        return [];
    }

    const modelIds = accessories.map(({ id }) => id);

    const { iso: locale, code: market } = activeMarket;
    const search: Query = {
        fieldList: ['model_id', 'product_price.count'],
        filterQuery: {
            modelId: modelIds.join(' OR '),
            modelMarket: market,
            objectType: 'product',
            modelAvailable: true,
        },
        start: 0,
        groupField: 'model_id',
    };

    const response: QueryResponse<AvailableModelAccessoriesResponse> =
        await getSearchResults(search, locale, activeMarket.code);

    const results = response?.results;

    const accessoriesWithStock = accessories.filter((accessory) => {
        const { productPriceCount } =
            results.find(
                ({ modelId }) => parseInt(modelId.values[0]) === accessory.id
            ) ?? {};

        const count = productPriceCount?.values[0] ?? 0;

        return Number(count) > 0;
    });

    return accessoriesWithStock;
};

export async function getSuperSkus(
    modelId: number,
    marketCode: MarketCode
): Promise<SuperSkusData[]> {
    const { data } = await client.get<SuperSkusData[]>('/search/super-skus', {
        headers: {
            'X-Market': marketCode,
        },
        params: {
            modelId,
        },
    });

    return data;
}

// eslint-disable-next-line @mpb-com/mpb/pure-request-functions -- Legacy code: do not use as an example.
export const getJustAddedItems = async (
    activeMarket: Market,
    filter?: Record<string, string>
) => {
    const query: Query = {
        rows: 15,
        fieldList: [
            'model_name',
            'product_price',
            'product_id',
            'model_url_segment',
            'product_condition',
            'product_shutter_count',
            'product_hour_count',
            'product_battery_charge_count',
            'product_sku',
            'product_images',
        ],
        filterQuery: {
            objectType: 'product',
            productConditionStarRating: '[1 TO 5]',
            productMarket: activeMarket.code,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
            ...(filter ? filter : {}),
        },
        sort: {
            productLastOnline: 'DESC',
        },
    };

    const response = await getSearchResults(
        query,
        activeMarket.iso,
        activeMarket.code
    );

    return response.results as Partial<SearchListingProduct>[];
};

/**
 * Get search results form the search service for the given query and locale.
 *
 * @format
 * @param {Query} search the search query
 * @param {string} locale The locale to search for results in. E.g. en_GB
 * @returns The search response object
 */

export async function getSearchResults<T>(
    search: Query,
    locale: string,
    marketCode: MarketCode
): Promise<QueryResponse<T>> {
    // eslint-disable-next-line @mpb-com/mpb/todo-requires-ticket -- Legacy code: do not use as an example.
    // TODO - update when modelOperation is added to search-service
    // overrides the product/model (sets to EU) market for EU markets only.
    if (europeanMarkets.includes(marketCode)) {
        if (search?.filterQuery?.productMarket) {
            search.filterQuery.productMarket = euMarket.code;
        }

        if (search?.filterQuery?.modelMarket) {
            search.filterQuery.modelMarket = euMarket.code;
        }
    }

    const queryParams = getSearchQueryParams(search, marketCode);

    const response = await client.get(
        `/search-service/product/query/?${queryParams}`,
        {
            headers: {
                'Content-Language': locale,
            },
        }
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
    return response.data;
}
